// src/admin/AdminExchangeRates.js
import React, { useState, useEffect } from 'react';
import {  
  doc, 
  setDoc, 
  getDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';

// Same currencies as your CurrencyConverter component
const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'KRW', symbol: '₩', name: 'Korean Won' },
  { code: 'ALL', symbol: 'L', name: 'Albanian Lek' }
];

function AdminExchangeRates() {
  const { currentUser } = useAuth();
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [rates, setRates] = useState({});
  const [editedRates, setEditedRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [message, setMessage] = useState('');
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Load current rates from Firestore
  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    try {
      setLoading(true);
      const ratesDoc = await getDoc(doc(db, 'settings', 'exchangeRates'));
      
      if (ratesDoc.exists()) {
        const data = ratesDoc.data();
        setBaseCurrency(data.baseCurrency || 'USD');
        setRates(data.rates || {});
        setEditedRates(data.rates || {});
        setLastUpdated(data.lastUpdated?.toDate());
      } else {
        // Initialize with default rates (USD as base)
        const defaultRates = {
          USD: 1,
          EUR: 0.92,
          GBP: 0.79,
          AUD: 1.55,
          CAD: 1.36,
          JPY: 149.5,
          CHF: 0.91,
          CNY: 7.26,
          INR: 82.0,
          KRW: 1329
        };
        setRates(defaultRates);
        setEditedRates(defaultRates);
        setBaseCurrency('USD');
      }
    } catch (error) {
      console.error('Error loading rates:', error);
      setMessage('Error loading rates');
    } finally {
      setLoading(false);
    }
  };

  // Convert rates to new base currency
  const convertToNewBase = (oldRates, oldBase, newBase) => {
    const newRates = {};
    
    // Get the conversion factor (how much 1 unit of old base = in new base)
    const conversionFactor = oldRates[newBase] || 1;
    
    // Convert all rates
    CURRENCIES.forEach(currency => {
      if (currency.code === newBase) {
        newRates[currency.code] = 1;
      } else if (currency.code === oldBase) {
        newRates[currency.code] = 1 / conversionFactor;
      } else {
        // Convert through the old base
        const oldRate = oldRates[currency.code] || 1;
        newRates[currency.code] = oldRate / conversionFactor;
      }
    });
    
    return newRates;
  };

  const handleBaseCurrencyChange = (newBase) => {
    if (newBase === baseCurrency) return;
    
    // Convert all rates to the new base
    const convertedRates = convertToNewBase(editedRates, baseCurrency, newBase);
    
    setBaseCurrency(newBase);
    setEditedRates(convertedRates);
    setMessage(`Base currency changed to ${newBase}. Remember to save.`);
  };

  const fetchLatestRates = async () => {
    try {
      setFetching(true);
      setMessage('Fetching latest rates...');

      // Fetch with current base currency
      let apiUrl = `https://api.exchangerate-api.com/v4/latest/${baseCurrency}`;
      
      try {
        const response = await fetch(apiUrl);
        const data = await response.json();

        if (data && data.rates) {
          // Filter to only include our supported currencies
          const filteredRates = {};
          CURRENCIES.forEach(currency => {
            if (data.rates[currency.code]) {
              filteredRates[currency.code] = data.rates[currency.code];
            } else if (currency.code === baseCurrency) {
              filteredRates[currency.code] = 1;
            }
          });

          setEditedRates(filteredRates);
          setMessage('✓ Rates fetched successfully! Remember to save.');
        }
      } catch (apiError) {
        // Fallback: fetch EUR rates and convert
        console.log('Fetching with base currency failed, trying EUR...');
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
        const data = await response.json();

        if (data && data.rates) {
          // Get EUR rates first
          const eurRates = {};
          CURRENCIES.forEach(currency => {
            if (data.rates[currency.code]) {
              eurRates[currency.code] = data.rates[currency.code];
            }
          });
          eurRates['EUR'] = 1;

          // Convert to selected base currency
          const convertedRates = convertToNewBase(eurRates, 'EUR', baseCurrency);
          setEditedRates(convertedRates);
          setMessage('✓ Rates fetched and converted successfully! Remember to save.');
        }
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
      setMessage('❌ Error fetching rates. Please try again.');
    } finally {
      setFetching(false);
    }
  };

  // Trigger cloud function update
  const triggerCloudUpdate = async () => {
    try {
      setFetching(true);
      setMessage('Triggering cloud function update...');
      
      const functions = getFunctions();
      const manualUpdate = httpsCallable(functions, 'manualUpdateExchangeRates');
      
      const result = await manualUpdate({ baseCurrency });
      
      if (result.data.success) {
        setMessage('✓ Cloud function update completed!');
        // Reload the rates
        await loadRates();
      }
    } catch (error) {
      console.error('Error triggering manual update:', error);
      setMessage('❌ Failed to trigger cloud update');
    } finally {
      setFetching(false);
    }
  };

  const handleRateChange = (currency, value) => {
    // Allow only numbers and decimal point
    const numValue = value.replace(/[^0-9.]/g, '');
    setEditedRates(prev => ({
      ...prev,
      [currency]: numValue
    }));
  };

  const saveRates = async () => {
    try {
      setSaving(true);
      setMessage('Saving...');

      // Convert string values to numbers
      const numericRates = {};
      Object.keys(editedRates).forEach(key => {
        numericRates[key] = parseFloat(editedRates[key]) || 0;
      });

      // Always ensure base currency is 1
      numericRates[baseCurrency] = 1;

      // Save to Firestore
      await setDoc(doc(db, 'settings', 'exchangeRates'), {
        rates: numericRates,
        baseCurrency: baseCurrency,
        lastUpdated: serverTimestamp(),
        updatedBy: currentUser?.email || 'admin',
        source: 'manual'
      });

      setRates(numericRates);
      setLastUpdated(new Date());
      setMessage('✓ Rates saved successfully!');

      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving rates:', error);
      setMessage('❌ Error saving rates');
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    setEditedRates(rates);
    setMessage('Changes reset');
    setTimeout(() => setMessage(''), 2000);
  };

  const hasChanges = JSON.stringify(rates) !== JSON.stringify(editedRates);

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading exchange rates...</div>;
  }

  return (
    <div style={{ 
      padding: isMobile ? '10px' : '20px', 
      maxWidth: '900px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <h2 style={{ 
        marginBottom: isMobile ? '15px' : '20px', 
        color: '#333',
        fontSize: isMobile ? '18px' : '24px'
      }}>Exchange Rate Management</h2>
      
      {/* Status Bar with Base Currency Selector */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: isMobile ? '10px' : '15px',
        borderRadius: isMobile ? '6px' : '8px',
        marginBottom: isMobile ? '15px' : '20px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: isMobile ? '10px' : '15px'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? '8px' : '15px'
          }}>
            <div>
              <strong>Base Currency:</strong>{' '}
              <select
                value={baseCurrency}
                onChange={(e) => handleBaseCurrencyChange(e.target.value)}
                style={{
                  padding: isMobile ? '8px' : '5px 10px',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  backgroundColor: 'white',
                  fontSize: isMobile ? '13px' : '14px',
                  cursor: 'pointer',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {CURRENCIES.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: isMobile ? '12px' : '14px' }}>
              <strong>Last Updated:</strong>{' '}
              {lastUpdated ? lastUpdated.toLocaleString() : 'Never'}
            </div>
          </div>
          {message && (
            <div style={{
              padding: isMobile ? '4px 8px' : '5px 10px',
              borderRadius: '4px',
              backgroundColor: message.includes('✓') ? '#d4edda' : 
                             message.includes('❌') ? '#f8d7da' : '#fff3cd',
              color: message.includes('✓') ? '#155724' : 
                     message.includes('❌') ? '#721c24' : '#856404',
              fontSize: isMobile ? '12px' : '14px',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              {message}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '10px',
        marginBottom: isMobile ? '15px' : '20px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={fetchLatestRates}
          disabled={fetching}
          style={{
            padding: isMobile ? '8px 12px' : '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: fetching ? 'not-allowed' : 'pointer',
            opacity: fetching ? 0.6 : 1,
            fontSize: isMobile ? '13px' : '14px',
            minHeight: isMobile ? '36px' : 'auto'
          }}
        >
          {fetching ? 'Fetching...' : `🔄 Fetch Latest ${baseCurrency} Rates`}
        </button>

        <button
          onClick={triggerCloudUpdate}
          disabled={fetching}
          style={{
            padding: isMobile ? '8px 12px' : '10px 20px',
            backgroundColor: '#6f42c1',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: fetching ? 'not-allowed' : 'pointer',
            opacity: fetching ? 0.6 : 1,
            fontSize: isMobile ? '13px' : '14px',
            minHeight: isMobile ? '36px' : 'auto'
          }}
        >
          ☁️ Trigger Cloud Update
        </button>

        <button
          onClick={saveRates}
          disabled={saving || !hasChanges}
          style={{
            padding: isMobile ? '8px 12px' : '10px 20px',
            backgroundColor: hasChanges ? '#28a745' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: saving || !hasChanges ? 'not-allowed' : 'pointer',
            opacity: saving || !hasChanges ? 0.6 : 1,
            fontSize: isMobile ? '13px' : '14px',
            minHeight: isMobile ? '36px' : 'auto'
          }}
        >
          {saving ? 'Saving...' : '💾 Save Changes'}
        </button>

        {hasChanges && (
          <button
            onClick={resetChanges}
            style={{
              padding: isMobile ? '8px 12px' : '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              minHeight: isMobile ? '36px' : 'auto'
            }}
          >
            ↩️ Reset Changes
          </button>
        )}
      </div>

      {/* Exchange Rates Table */}
      <div style={{ 
        overflowX: 'auto',
        marginLeft: isMobile ? '-10px' : '0',
        marginRight: isMobile ? '-10px' : '0',
        paddingLeft: isMobile ? '10px' : '0',
        paddingRight: isMobile ? '10px' : '0',
        WebkitOverflowScrolling: 'touch',
        width: isMobile ? 'calc(100% + 20px)' : '100%'
      }}>
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: isMobile ? '6px' : '8px',
          overflow: 'hidden',
          minWidth: isMobile ? '700px' : 'auto'
        }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: isMobile ? '12px' : '14px'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ 
                padding: isMobile ? '6px 4px' : '12px', 
                textAlign: 'left', 
                borderBottom: '2px solid #dee2e6',
                fontSize: isMobile ? '11px' : '14px',
                fontWeight: 'bold'
              }}>
                Currency
              </th>
              <th style={{ 
                padding: isMobile ? '6px 4px' : '12px', 
                textAlign: 'left', 
                borderBottom: '2px solid #dee2e6',
                fontSize: isMobile ? '11px' : '14px',
                fontWeight: 'bold'
              }}>
                Symbol
              </th>
              <th style={{ 
                padding: isMobile ? '6px 4px' : '12px', 
                textAlign: 'left', 
                borderBottom: '2px solid #dee2e6',
                fontSize: isMobile ? '11px' : '14px',
                fontWeight: 'bold'
              }}>
                Rate (1 {baseCurrency} = ?)
              </th>
              <th style={{ 
                padding: isMobile ? '6px 4px' : '12px', 
                textAlign: 'left', 
                borderBottom: '2px solid #dee2e6',
                fontSize: isMobile ? '11px' : '14px',
                fontWeight: 'bold'
              }}>
                Current Saved
              </th>
              <th style={{ 
                padding: isMobile ? '6px 4px' : '12px', 
                textAlign: 'center', 
                borderBottom: '2px solid #dee2e6',
                fontSize: isMobile ? '11px' : '14px',
                fontWeight: 'bold'
              }}>
                Changed
              </th>
            </tr>
          </thead>
          <tbody>
            {CURRENCIES.map((currency, index) => {
              const isChanged = rates[currency.code] !== parseFloat(editedRates[currency.code]);
              const isBase = currency.code === baseCurrency;
              
              return (
                <tr 
                  key={currency.code}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa'
                  }}
                >
                  <td style={{ 
                    padding: isMobile ? '6px 4px' : '12px', 
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: isBase ? 'bold' : 'normal',
                    fontSize: isMobile ? '11px' : '14px'
                  }}>
                    {currency.name} ({currency.code})
                    {isBase && (
                      <span style={{ 
                        marginLeft: '10px',
                        padding: '2px 6px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        borderRadius: '3px',
                        fontSize: '11px'
                      }}>
                        BASE
                      </span>
                    )}
                  </td>
                  <td style={{ 
                    padding: isMobile ? '6px 4px' : '12px', 
                    borderBottom: '1px solid #dee2e6',
                    fontSize: isMobile ? '16px' : '18px'
                  }}>
                    {currency.symbol}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                    {isBase ? (
                      <span style={{ 
                        color: '#007bff',
                        fontWeight: 'bold'
                      }}>
                        1.0000 (Base)
                      </span>
                    ) : (
                      <input
                        type="text"
                        value={editedRates[currency.code] || ''}
                        onChange={(e) => handleRateChange(currency.code, e.target.value)}
                        style={{
                          padding: isMobile ? '4px 6px' : '6px 10px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          width: isMobile ? '80px' : '120px',
                          backgroundColor: isChanged ? '#fff3cd' : 'white',
                          fontSize: isMobile ? '11px' : '14px'
                        }}
                        placeholder="0.00"
                      />
                    )}
                  </td>
                  <td style={{ 
                    padding: isMobile ? '6px 4px' : '12px', 
                    borderBottom: '1px solid #dee2e6',
                    color: '#6c757d',
                    fontSize: isMobile ? '11px' : '14px'
                  }}>
                    {rates[currency.code]?.toFixed(4) || '-'}
                  </td>
                  <td style={{ 
                    padding: isMobile ? '6px 4px' : '12px', 
                    borderBottom: '1px solid #dee2e6',
                    textAlign: 'center',
                    fontSize: isMobile ? '11px' : '14px'
                  }}>
                    {isChanged && (
                      <span style={{ color: '#ffc107', fontSize: '18px' }}>●</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Quick Conversion Preview */}
      <div style={{
        marginTop: isMobile ? '15px' : '20px',
        padding: isMobile ? '10px' : '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: isMobile ? '6px' : '8px'
      }}>
        <h4 style={{ 
          marginTop: 0, 
          marginBottom: isMobile ? '10px' : '15px',
          fontSize: isMobile ? '14px' : '18px'
        }}>💱 Quick Conversion Preview</h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: isMobile ? '6px' : '10px'
        }}>
          {CURRENCIES.filter(c => c.code !== baseCurrency).map(currency => {
            const rate = parseFloat(editedRates[currency.code]) || 0;
            return (
              <div key={currency.code} style={{
                padding: isMobile ? '6px' : '8px',
                backgroundColor: 'white',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                fontSize: isMobile ? '12px' : '14px'
              }}>
                <strong>100 {baseCurrency}</strong> = {(100 * rate).toFixed(2)} {currency.code}
              </div>
            );
          })}
        </div>
      </div>

      {/* Info Box */}
      <div style={{
        marginTop: isMobile ? '15px' : '20px',
        padding: isMobile ? '10px' : '15px',
        backgroundColor: '#e9ecef',
        borderRadius: isMobile ? '6px' : '8px',
        fontSize: isMobile ? '12px' : '14px',
        color: '#495057'
      }}>
        <h4 style={{ 
          marginTop: 0, 
          marginBottom: isMobile ? '8px' : '10px',
          fontSize: isMobile ? '14px' : '18px'
        }}>ℹ️ How it works:</h4>
        <ul style={{ 
          marginLeft: isMobile ? '15px' : '20px', 
          marginBottom: 0,
          lineHeight: isMobile ? '1.4' : '1.5'
        }}>
          <li>Select your preferred base currency from the dropdown</li>
          <li>Click "Fetch Latest Rates" to get current exchange rates from the API</li>
          <li>Click "Trigger Cloud Update" to run the cloud function manually</li>
          <li>Manually adjust any rates as needed</li>
          <li>Click "Save Changes" to update the database</li>
          <li>The app will use these saved rates for all currency conversions</li>
          <li>Rates automatically update daily at 2 AM UTC via cloud function</li>
        </ul>
      </div>
    </div>
  );
}

export default AdminExchangeRates;